import { z } from 'zod';

/**
 * Checklist dos eventos (docs/eventos, decidido pelo GrowuP a 20-10-2026): as
 * tarefas de preparacao e de fecho, sem zona e sem hora.
 *
 *   - `/api/site/events/:id/checklist/...`  back-office (`events.manage`)
 *   - `/api/me/event-tasks/...`              os meus itens (e os dos meus jovens)
 */

export const checklistSectionSchema = z.enum(['prep', 'wrap_up']);
export type ChecklistSection = z.infer<typeof checklistSectionSchema>;

export const CHECKLIST_SECTION_LABELS: Record<ChecklistSection, string> = {
  prep: 'Preparação',
  wrap_up: 'Depois do evento',
};

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'aaaa-mm-dd');

export const checklistPersonSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  /** Voluntario confirmado neste evento (os outros sao staff). */
  volunteer: z.boolean(),
});
export type ChecklistPerson = z.infer<typeof checklistPersonSchema>;

export const checklistItemSchema = z.object({
  id: z.string().uuid(),
  section: checklistSectionSchema,
  title: z.string(),
  notes: z.string(),
  dueOn: z.string().nullable(),
  assignee: checklistPersonSchema.nullable(),
  /** O custo ligado; so vem a quem tem `events.budget`. */
  financeEntry: z.object({ id: z.string().uuid(), description: z.string() }).nullable(),
  doneAt: z.string().nullable(),
  doneBy: z.string().nullable(),
  /** Prazo antes de hoje (Lisboa) e ainda por fazer. */
  overdue: z.boolean(),
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

export const eventChecklistSchema = z.object({
  eventId: z.string().uuid(),
  items: z.array(checklistItemSchema),
  summary: z.object({
    total: z.number().int(),
    done: z.number().int(),
    overdue: z.number().int(),
    /** Dias ate ao primeiro dia do evento; negativo depois de comecar. */
    daysToEvent: z.number().int(),
  }),
  /** Custos do evento que se podem ligar; vazio sem `events.budget`. */
  costs: z.array(z.object({ id: z.string().uuid(), description: z.string() })),
});
export type EventChecklist = z.infer<typeof eventChecklistSchema>;

export const checklistItemInputSchema = z.object({
  section: checklistSectionSchema,
  title: z.string().trim().min(1, 'escreve o que ha a fazer').max(140),
  notes: z.string().trim().max(2000).default(''),
  dueOn: dateSchema.nullable().default(null),
  assigneeAccountId: z.string().uuid().nullable().default(null),
  financeEntryId: z.string().uuid().nullable().default(null),
});
export type ChecklistItemInput = z.input<typeof checklistItemInputSchema>;

export const checklistDoneInputSchema = z.object({ done: z.boolean() });

export const checklistOrderInputSchema = z.object({
  section: checklistSectionSchema,
  ids: z.array(z.string().uuid()).max(500),
});
export type ChecklistOrderInput = z.infer<typeof checklistOrderInputSchema>;

export const checklistCopyInputSchema = z.object({ fromEventId: z.string().uuid() });

/** Um item meu (ou de um jovem meu) em `growup.gg/conta/eventos`. */
export const myEventTaskSchema = z.object({
  id: z.string().uuid(),
  section: checklistSectionSchema,
  title: z.string(),
  notes: z.string(),
  dueOn: z.string().nullable(),
  done: z.boolean(),
  overdue: z.boolean(),
  /** Login do jovem, quando o item e de um jovem meu. */
  youthLogin: z.string().nullable(),
  event: z.object({ slug: z.string(), title: z.string(), startsAt: z.string() }),
});
export type MyEventTask = z.infer<typeof myEventTaskSchema>;
